import { useInfiniteQuery } from '@tanstack/react-query';
import { FC, useMemo } from 'react';
import styled from 'styled-components';

import InfiniteLoader from '@/components/InfiniteLoader';
import { PageHeading } from '@/components/Layout/Parts';
import { DQFeedRecord, PaginatedResponse } from '@/types/resources';
import { api } from '@/utils/api';

import FeedCard from './components/FeedCard';
import FeedCardSkeleton from './components/FeedCardSkeleton';

const StyledPageHeading = styled(PageHeading)`
  text-align: center;
`;

const Feed = styled.div`
  display: flex;
  flex-direction: column; /* stack cards vertically */
  align-items: center; /* center cards horizontally */
  gap: 20px;
`;

const DQFeed: FC = () => {
  const fetchSearches = ({ pageParam = '/v1/editorial/queue/feed' }) => {
    return api.get<never, PaginatedResponse<DQFeedRecord>>(pageParam);
  };

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isLoading } =
    useInfiniteQuery<PaginatedResponse<DQFeedRecord>>(
      ['dq_feed'],
      fetchSearches,
      {
        getNextPageParam: (lastPage) => {
          return lastPage.links.next;
        },
      },
    );

  const posts = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<DQFeedRecord[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  return (
    <>
      <StyledPageHeading>DQ Feed</StyledPageHeading>
      <Feed>
        {posts.map((p) => (
          <FeedCard post={p} key={p.id} />
        ))}
        {isLoading && (
          <>
            <FeedCardSkeleton />
            <FeedCardSkeleton />
            <FeedCardSkeleton />
          </>
        )}
        <InfiniteLoader
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
          component={<FeedCardSkeleton />}
        />
      </Feed>
    </>
  );
};

export default DQFeed;
